import { Stack, Box, Typography } from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  timelineItemClasses,
} from "@mui/lab";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import FormHeader from "src/components/Displays/FormHeader";
import LabelTitle from "src/components/Displays/LabelTitle";
import PageTitle from "src/components/Displays/PageTitle";
import ProgressBarTitle from "src/components/Displays/ProgressBarTitle";
import Page from "src/components/Layout/Page";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import {
  SESSION_KEYS,
  USER_PROFILE_DATA,
  FUNNEL_ORDER_RESPONSE,
} from "src/consts/session";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { Tariff } from "src/models/clientsModel";

const Conclusion = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [getSessionStorageItem] = useSessionStorage<USER_PROFILE_DATA>(
    SESSION_KEYS.USER_PROFILE_DATA,
  );
  const sessionData = getSessionStorageItem();

  const [getFunnelOrderResponse] = useSessionStorage<FUNNEL_ORDER_RESPONSE>(
    SESSION_KEYS.FUNNEL_ORDER_RESPONSE,
  );

  const [getTariffs] = useSessionStorage<Tariff[]>(SESSION_KEYS.TARIFFS);

  const handleFinishClick = () => {
    let redirectUrl = getFunnelOrderResponse()?.redirectUrl;

    if (redirectUrl && redirectUrl !== "") {
      window.location.href = redirectUrl;
      return;
    }
    navigate("/");
  };

  return (
    <Page pageTitle="" pageSubTitle="">
      <Stack
        gap={4}
        sx={{
          mt: { xs: 8, sm: 0 },
        }}
      >
        <PageTitle
          text={t("page.conclusion.title", {
            name: getTariffs()?.[0]?.tariffDisplayName || "",
            tarif: getTariffs()?.[0]?.tariffProviderDisplayName || "",
          })}
        />

        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography
            variant="h4"
            sx={(theme) => ({
              fontFamily: "isonType",
              fontWeight: 500,
              mb: 3,
              fontSize: "28px",
              color: theme.palette.app.primaryFont,
            })}
          >
            {t("page.conclusion.nextStepsTitle")}
          </Typography>
          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
              [`& .${timelineItemClasses.root}`]: {
                minHeight: { xs: "auto", sm: "26px" },
              },
              margin: 0,
              padding: 0,
              mt: 10,
            }}
          >
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot
                  sx={(theme) => ({
                    padding: 0,
                    width: "8px",
                    height: "8px",
                    background: theme.palette.app.primaryFont,
                    margin: 0,
                  })}
                />
                <TimelineConnector
                  sx={(theme) => ({
                    border: `1px dashed ${theme.palette.app.primaryFont}`,
                    backgroundColor: "transparent",
                    minHeight: { xs: "20px", sm: "8px" },
                  })}
                />
              </TimelineSeparator>
              <TimelineContent
                sx={{
                  padding: { xs: "0 0 20px 12px", sm: "0 0 4px 12px" },
                  mt: -1,
                }}
              >
                <Typography
                  sx={(theme) => ({
                    fontSize: "13px",
                    fontWeight: 700,
                    color: theme.palette.app.primaryFont,
                    lineHeight: "1.2",
                  })}
                >
                  {t("page.conclusion.timelineItem1")}
                </Typography>
              </TimelineContent>
            </TimelineItem>

            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot
                  sx={(theme) => ({
                    padding: 0,
                    width: "8px",
                    height: "8px",
                    background: theme.palette.app.primaryFont,
                    margin: 0,
                  })}
                />
                <TimelineConnector
                  sx={(theme) => ({
                    background: theme.palette.app.primaryFont,
                    width: "2px",
                    minHeight: { xs: "20px", sm: "8px" },
                  })}
                />
              </TimelineSeparator>
              <TimelineContent
                sx={{
                  padding: { xs: "0 0 20px 12px", sm: "0 0 4px 12px" },
                  mt: { xs: -2.5, sm: -1 },
                }}
              >
                <Typography
                  sx={(theme) => ({
                    fontSize: "13px",
                    fontWeight: 400,
                    color: theme.palette.app.primaryFont,
                    lineHeight: "1.2",
                  })}
                >
                  {t("page.conclusion.timelineItem2")}
                </Typography>
              </TimelineContent>
            </TimelineItem>

            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot
                  sx={(theme) => ({
                    padding: 0,
                    width: "8px",
                    height: "8px",
                    background: theme.palette.app.primaryFont,
                    margin: 0,
                  })}
                />
              </TimelineSeparator>
              <TimelineContent
                sx={{
                  padding: "0 0 0 12px",
                  mt: { xs: -2.5, sm: -1 },
                }}
              >
                <Typography
                  sx={(theme) => ({
                    fontSize: "13px",
                    fontWeight: 400,
                    color: theme.palette.app.primaryFont,
                    lineHeight: "1.2",
                  })}
                >
                  {t("page.conclusion.timelineItem3")}
                </Typography>
              </TimelineContent>
            </TimelineItem>
          </Timeline>
        </Box>

        <ProgressBarTitle
          text={t("page.conclusion.subtitle1", {
            name: getTariffs()?.[0]?.tariffDisplayName || "",
            tarif: getTariffs()?.[0]?.tariffProviderDisplayName || "",
          })}
        />

        <Stack
          sx={(theme) => ({
            borderRadius: 2,
            py: 2,
            px: 3,
            backgroundColor: theme.palette.green[100],
          })}
        >
          <LabelTitle
            text={t("page.conclusion.email", {
              tarif: getTariffs()?.[0]?.tariffProviderDisplayName || "",
            })}
          />
          <FormHeader
            sx={(theme) => ({
              color: theme.palette.green[50],
            })}
            text={sessionData?.email || ""}
          />
        </Stack>

        <PrimaryButton
          sx={(theme) => ({
            mt: 4,
            bgcolor: theme.palette.secondaryButton.background,
            color: theme.palette.secondaryButton.text,
            "&:hover": {
              backgroundColor: theme.palette.secondaryButton.background,
            },
          })}
          onClick={handleFinishClick}
          text={t("page.conclusion.button")}
        />
      </Stack>
    </Page>
  );
};

export default Conclusion;
